import Link from 'next/link';
import { memo, useMemo } from 'react';

import { ExternalLinkIcon } from '@air/design-icons';
import { FileStatusIconCompleted, FileStatusSubtitle, FileStatusTitle } from '@air/feature-file-status/ui';

import { type CaptionVideoTaskMetadata } from '~/components/CaptionVideoTasks/types';
import { getTaskPaneFileStatusIconSize } from '~/components/FileStatusTrackingPane/getTaskPaneFileStatusIconSize';
import { PaneItemTW } from '~/components/FileStatusTrackingPane/PaneItemTW';
import { TaskPaneActionLink } from '~/components/FileStatusTrackingPane/TaskPaneActionLink';
import { TaskPaneDismissButton } from '~/components/FileStatusTrackingPane/TaskPaneDismissButton';
import { createAllContentAssetRoute, createBoardClipRoute } from '~/constants/routes';
import { useTemporaryFeatureFlags } from '~/hooks/useTemporaryFeatureFlags';

export type CaptionVideoTaskPanelItemCompletedProps = {
  metadata: CaptionVideoTaskMetadata;
  onClear: () => void;
};

const getCaptionCompletedTitle = ({ isAssetVersion, isUi2026 }: { isAssetVersion: boolean; isUi2026: boolean }) => {
  if (isUi2026) {
    return isAssetVersion ? 'Caption version saved' : 'Caption asset saved';
  }

  return isAssetVersion ? 'Captions saved as new version' : 'Captions saved as new asset';
};

export const CaptionVideoTaskPanelItemCompleted = memo(
  ({ metadata, onClear }: CaptionVideoTaskPanelItemCompletedProps) => {
    const { ui2026 } = useTemporaryFeatureFlags();
    const clipId = metadata.versionId;
    const boardId = metadata.saveResultAs?.options?.boardId;
    const isAssetVersion = metadata.saveResultAs?.as === 'assetVersion';

    const clipRoute = useMemo(() => {
      if (!clipId) {
        return undefined;
      }

      if (boardId) {
        return createBoardClipRoute({ clipId, boardId, boardTitle: '' });
      }

      return createAllContentAssetRoute(clipId);
    }, [clipId, boardId]);

    return (
      <PaneItemTW
        avatar={<FileStatusIconCompleted size={getTaskPaneFileStatusIconSize(ui2026)} />}
        title={
          <FileStatusTitle data-testid="CAPTION_VIDEO_PANEL_COMPLETED_TITLE">
            {getCaptionCompletedTitle({ isAssetVersion, isUi2026: ui2026 })}
          </FileStatusTitle>
        }
        subtitle={
          ui2026 || !clipRoute ? undefined : (
            <FileStatusSubtitle className="truncate">
              <Link
                href={clipRoute}
                className="font-semibold ads:text-accent text-12 text-altitude-9 ads:hover:text-accent-hover hover:text-altitude-11 hover:underline"
              >
                View asset
              </Link>
            </FileStatusSubtitle>
          )
        }
        buttons={
          <>
            {ui2026 && clipRoute ? (
              <TaskPaneActionLink
                aria-label={isAssetVersion ? 'View caption version' : 'View caption asset'}
                href={clipRoute}
                icon={<ExternalLinkIcon size="sm" />}
              >
                View
              </TaskPaneActionLink>
            ) : null}
            <TaskPaneDismissButton label="Dismiss" onClick={onClear} />
          </>
        }
      />
    );
  },
);

CaptionVideoTaskPanelItemCompleted.displayName = 'CaptionVideoTaskPanelItemCompleted';
